
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Search, Filter, ChevronDown } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ListingCard from '@/components/ListingCard';
import { BreedSelector } from '@/components/BreedSelector';
import { LocationAutocomplete } from '@/components/dashboard/LocationAutocomplete';
import { LocationRangeSlider } from '@/components/LocationRangeSlider';
import { useMarketplaceListings } from '@/hooks/useMarketplaceListings';
import { useBreeds } from '@/hooks/useBreeds';
import BreedingValueFilters, { type AppliedFilters } from '@/components/marketplace/BreedingValueFilters';

const Marketplace = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBreed, setSelectedBreed] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationCoordinates, setLocationCoordinates] = useState<[number, number] | null>(null);
  const [rangeKm, setRangeKm] = useState(50);
  const [selectedType, setSelectedType] = useState('all');
  const [locationType, setLocationType] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const emptyAppliedFilters: AppliedFilters = { priceMin: '', priceMax: '', breedingValues: {} };
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState<AppliedFilters>(emptyAppliedFilters);

  const listingsPerPage = 12;

  // Fetch listings using the hook
  const { data, isLoading, error } = useMarketplaceListings({
    searchTerm,
    selectedBreed,
    selectedLocation,
    locationCoordinates,
    rangeKm,
    locationType,
    selectedType,
    page: currentPage,
    limit: listingsPerPage,
    priceMin: appliedAdvancedFilters.priceMin,
    priceMax: appliedAdvancedFilters.priceMax,
    breedingValueFilters: appliedAdvancedFilters.breedingValues,
  });

  // Fetch breeds using the new hook
  const { data: breeds, isLoading: breedsLoading } = useBreeds();

  const listings = data?.listings || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / listingsPerPage);

  // Set initial filter state based on screen size
  useEffect(() => {
    let previousIsDesktop: boolean | null = null;
    
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024; // lg breakpoint
      
      // Only update filter state on actual desktop/mobile transitions
      if (previousIsDesktop !== null && previousIsDesktop !== desktop) {
        // Switching TO desktop: always open filters
        if (desktop) {
          setIsFiltersOpen(true);
        }
        // Switching TO mobile: keep current state (don't force close)
      } else if (previousIsDesktop === null) {
        // Initial load: close on mobile, open on desktop
        setIsFiltersOpen(desktop);
      }
      
      setIsDesktop(desktop);
      previousIsDesktop = desktop;
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBreed, selectedLocation, locationCoordinates, rangeKm, selectedType, appliedAdvancedFilters]);

  const types = [
    { value: 'all', label: 'All' },
    { value: 'bull', label: 'Bulls' },
    { value: 'cow', label: 'Cows' },
  ];

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBreed('');
    setSelectedLocation('');
    setLocationCoordinates(null);
    setLocationType(null);
    setRangeKm(50);
    setSelectedType('all');
    setCurrentPage(1);
    setAppliedAdvancedFilters(emptyAppliedFilters);
  };

  const handleLocationChange = (location: string, coordinates?: [number, number], locType?: string) => {
    setSelectedLocation(location);
    setLocationCoordinates(coordinates || null);
    setLocationType(locType || null);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-lg shadow-sm">
              {/* Collapsible header for mobile, always visible on desktop */}
              <Collapsible open={isFiltersOpen}>
                <div className="flex items-center justify-between w-full p-6">
                  <div className="flex items-center">
                    <Filter className="w-5 h-5 mr-2" />
                    <h2 className="text-lg font-semibold">Filters</h2>
                  </div>
                  {!isDesktop && (
                    <CollapsibleTrigger asChild>
                      <button
                        className="p-1 hover:bg-gray-100 rounded"
                        onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                        type="button"
                      >
                        <ChevronDown className="w-5 h-5" />
                      </button>
                    </CollapsibleTrigger>
                  )}
                </div>
                
                <CollapsibleContent className="lg:block">
                  <div className="px-6 pb-6 space-y-6">
                    <div data-filter-content>
                      <label className="block text-sm font-medium mb-2">Search</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Search listings..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div data-filter-content>
                      <label className="block text-sm font-medium mb-2">Breed</label>
                      <BreedSelector
                        value={selectedBreed}
                        onValueChange={setSelectedBreed}
                        placeholder="All breeds"
                        className="w-full"
                      />
                      {selectedBreed && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setSelectedBreed('')}
                          className="mt-1 h-6 px-2 text-xs"
                        >
                          Clear breed
                        </Button>
                      )}
                    </div>

                    <div data-filter-content>
                      <LocationAutocomplete
                        value={selectedLocation}
                        onChange={handleLocationChange}
                        placeholder="Search for a location..."
                      />
                      {selectedLocation && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleLocationChange('', undefined)}
                          className="mt-1 h-6 px-2 text-xs"
                        >
                          Clear location
                        </Button>
                      )}
                    </div>

                    {selectedLocation && locationCoordinates && (locationType === 'place' || locationType === 'locality') && (
                      <div>
                        <LocationRangeSlider
                          value={rangeKm}
                          onChange={setRangeKm}
                        />
                      </div>
                    )}

                    <BreedingValueFilters
                      appliedFilters={appliedAdvancedFilters}
                      onApply={(filters) => setAppliedAdvancedFilters(filters)}
                      onClear={() => setAppliedAdvancedFilters(emptyAppliedFilters)}
                    />

                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={clearFilters}
                    >
                      Clear All Filters
                    </Button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>

          {/* Listings Section */}
          <div className="lg:w-3/4">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Marketplace</h1>
              <p className="text-gray-600">{totalCount} listings found</p>
            </div>

            {/* Type Tabs */}
            <Tabs value={selectedType} onValueChange={setSelectedType} className="mb-8">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                {types.map((type) => (
                  <TabsTrigger 
                    key={type.value} 
                    value={type.value}
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    {type.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {types.map((type) => (
                <TabsContent key={type.value} value={type.value} className="mt-0">
                  {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : error ? (
                    <div className="text-center py-12">
                      <p className="text-gray-600">Unable to load listings at the moment.</p>
                    </div>
                  ) : listings.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-600">No listings found matching your criteria.</p>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {listings.map((listing) => (
                          <ListingCard key={listing.id} listing={listing} />
                        ))}
                      </div>
                      
                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="mt-8 flex justify-center">
                          <Pagination>
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious 
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                />
                              </PaginationItem>
                              
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                  pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = currentPage - 2 + i;
                                }
                                
                                return (
                                  <PaginationItem key={pageNum}>
                                    <PaginationLink
                                      onClick={() => handlePageChange(pageNum)}
                                      isActive={currentPage === pageNum}
                                      className="cursor-pointer"
                                    >
                                      {pageNum}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              })}
                              
                              <PaginationItem>
                                <PaginationNext 
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  className={currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Marketplace;
